import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { buildCartonQrText } from "@apv-erp/status";
import { formatNumber, getUploadUrl } from "@/lib/utils";

/**
 * Tem thùng thành phẩm số nhảy — khổ cuộn 80 × 150 mm, đọc ngang:
 * nội dung dựng ngang 150 × 80 mm rồi xoay 90° vào trang dọc. Thương hiệu lấy từ Cấu hình công ty (logo APV).
 */
export interface FinishedGoodsLabelData {
  labelId: string;
  labelCode: string;
  /** Thùng thứ x / tổng số thùng của lô */
  boxIndex: number;
  totalBoxes: number;
  boxQuantity: number;
  isLastBox: boolean;
  unit?: string | null;
  customerName?: string | null;
  orderCode?: string | null;
  customerPo?: string | null;
  productionOrderCode?: string | null;
  deliveryAddress?: string | null;
  productName?: string | null;
  productCode?: string | null;
  lotNumber?: string | null;
  packer?: string | null;
  /** dd/mm/yyyy */
  packingDate?: string | null;
}

export interface LabelBrand {
  logoUrl?: string;
  companyName?: string;
  phone?: string;
  website?: string;
}

const DEFAULT_LOGO = "/artpresto-logo.png";

/** Logo tải lên (/uploads, URL tuyệt đối) nằm trên máy chủ API; logo mặc định nằm trong /public của web */
function resolveLogoUrl(url?: string): string {
  if (!url) return DEFAULT_LOGO;
  return url.startsWith("http") || url.includes("/uploads/") ? getUploadUrl(url) : url;
}

export const LABEL_WIDTH_MM = 80;
export const LABEL_HEIGHT_MM = 150;

const field = (label: string, value: React.ReactNode, extra?: React.CSSProperties) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: "1.5mm", minWidth: 0, ...extra }}>
    <span style={{ fontSize: "8pt", color: "#475569", whiteSpace: "nowrap" }}>{label}</span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontSize: "10.5pt",
        fontWeight: 700,
        color: "#0f172a",
        borderBottom: "0.2mm dotted #94a3b8",
        lineHeight: 1.25,
        overflow: "hidden",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      }}
    >
      {value || " "}
    </span>
  </div>
);

export function FinishedGoodsLabel({ data, brand }: { data: FinishedGoodsLabelData; brand: LabelBrand }) {
  const qrText = buildCartonQrText({
    orderCode: data.orderCode || data.productionOrderCode,
    deliveryAddress: data.deliveryAddress,
    product: data.productCode || data.productName,
    packer: data.packer,
    packingDate: data.packingDate,
    boxQty: data.boxQuantity,
    labelCode: data.labelCode,
  });
  const orderLine = [data.orderCode, data.customerPo && data.customerPo !== data.orderCode ? `PO ${data.customerPo}` : null]
    .filter(Boolean)
    .join(" • ");
  const contact = [brand.phone?.trim(), brand.website?.replace(/^https?:\/\//, "").replace(/\/$/, "")].filter(Boolean).join(" • ");

  return (
    <div
      className="fg-label"
      data-label-code={data.labelCode}
      style={{
        width: `${LABEL_WIDTH_MM}mm`,
        height: `${LABEL_HEIGHT_MM}mm`,
        position: "relative",
        overflow: "hidden",
        background: "#fff",
        fontFamily: "Arial, Helvetica, sans-serif",
        color: "#0f172a",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: `${LABEL_HEIGHT_MM}mm`,
          height: `${LABEL_WIDTH_MM}mm`,
          transform: "rotate(90deg) translate(0, -100%)",
          transformOrigin: "0 0",
          padding: "3.5mm 4mm",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: "1.8mm",
        }}
      >
        {/* Thương hiệu + QC PASSED */}
        <div style={{ display: "flex", alignItems: "center", gap: "3mm", borderBottom: "0.4mm solid #0f172a", paddingBottom: "1.8mm" }}>
          {brand.logoUrl ? (
            <img
              src={resolveLogoUrl(brand.logoUrl)}
              alt="logo"
              style={{ height: "11mm", maxWidth: "30mm", objectFit: "contain" }}
              onError={(e) => {
                // Logo cấu hình không tải được → logo Artpresto có sẵn
                const img = e.currentTarget;
                const fallback = new URL(DEFAULT_LOGO, window.location.origin).href;
                if (img.src !== fallback) img.src = fallback;
              }}
            />
          ) : null}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "9.5pt", fontWeight: 800, textTransform: "uppercase", lineHeight: 1.15 }}>
              {brand.companyName || "ARTPRESTO VIỆT NAM"}
            </div>
            {contact ? <div style={{ fontSize: "7.5pt", color: "#334155", marginTop: "0.6mm" }}>{contact}</div> : null}
          </div>
          <div
            style={{
              border: "0.5mm solid #047857",
              color: "#047857",
              borderRadius: "1.5mm",
              padding: "0.8mm 2mm",
              textAlign: "center",
              lineHeight: 1.05,
              fontWeight: 900,
            }}
          >
            <div style={{ fontSize: "11pt" }}>QC</div>
            <div style={{ fontSize: "7pt", letterSpacing: "0.3mm" }}>PASSED</div>
          </div>
        </div>

        {/* Thông tin thùng */}
        <div style={{ display: "flex", gap: "3mm", flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "1.6mm" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 0.85fr", gap: "3mm" }}>
              {field("Khách hàng:", data.customerName)}
              {field("Số ĐH:", orderLine || data.productionOrderCode)}
            </div>
            {field("ĐCGH:", data.deliveryAddress)}
            {field("Tên sản phẩm:", [data.productName, data.productCode ? `(${data.productCode})` : null].filter(Boolean).join(" "))}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3mm" }}>
              {field("LSX:", data.productionOrderCode)}
              {field("Lô:", data.lotNumber)}
            </div>
          </div>
          <div style={{ width: "24mm", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.8mm" }}>
            <QRCodeSVG value={qrText} size={88} level="M" style={{ width: "22mm", height: "22mm" }} />
            <div style={{ fontSize: "6.5pt", fontFamily: "monospace", textAlign: "center", wordBreak: "break-all", lineHeight: 1.1 }}>
              {data.labelCode}
            </div>
          </div>
        </div>

        {/* Số lượng — Số thùng (số nhảy) — Ngày ĐG */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr 1fr 1fr",
            gap: "3mm",
            alignItems: "end",
            borderTop: "0.3mm solid #cbd5e1",
            paddingTop: "1.5mm",
          }}
        >
          <div>
            <div style={{ fontSize: "7.5pt", color: "#475569" }}>Số lượng</div>
            <div style={{ fontSize: "15pt", fontWeight: 900 }}>
              {formatNumber(data.boxQuantity)} <span style={{ fontSize: "9pt" }}>{data.unit || "PCS"}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: "7.5pt", color: "#475569" }}>Số thùng</div>
            <div style={{ fontSize: "15pt", fontWeight: 900 }} data-testid="label-box-no">
              {data.boxIndex}/{data.totalBoxes}
              {data.isLastBox && data.totalBoxes > 1 ? <span style={{ fontSize: "7pt", fontWeight: 700, marginLeft: "1mm" }}>(cuối)</span> : null}
            </div>
          </div>
          <div>
            <div style={{ fontSize: "7.5pt", color: "#475569" }}>Ngày ĐG</div>
            <div style={{ fontSize: "11pt", fontWeight: 800 }}>{data.packingDate || ""}</div>
            {data.packer ? <div style={{ fontSize: "7pt", color: "#475569" }}>Đóng gói: {data.packer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FinishedGoodsLabel;
